/**
 * xr_profile — the graphics a VR session runs with, decided setting by setting (XR P0.4, 2026-09-28). Pure.
 *
 * WHY A DECISION PER KEY. The first VR profile was a hand list of twenty values over a TierPreset that has since
 * grown to 136: every realism and shader flag added after it rode into the headset at whatever the desktop tier
 * said. `VR_DECISIONS` is typed over `Required<TierPreset>`, so a new preset key does not COMPILE until someone
 * decides what VR does with it — and `verify:xr-profile` checks the same list against the interface source.
 *
 * TWO KINDS OF DECISION.
 *  - `vr`: the value while presenting (one value, or one per VR level [quality, balanced, performance]). Only
 *    settings that change LIVE on `atlas:quality-changed` (or that only feed the post chain, which XR bypasses)
 *    may be `vr` — the session boundary restores them, and every consumer rescales on the same event.
 *  - `keep`: the player's value stays. Either a PREFERENCE (colourblind, tone curve, exposure, FOV), or a setting
 *    that only lands after a reload / at the next world / when a material is parsed — a VR value there would leave
 *    the desktop drawing the headset's choice until then (PC VR exits back to the same tab). On a standalone
 *    headset those are the headset's OWN saved preset, which `detectDefaultTier` starts at 'low'.
 *
 * Budgets (Meta WebXR guidance, report §2.2): 72 Hz = 13.9 ms a frame, ~8–9 ms of it rendering; ~100 draw calls
 * comfortable, 200 tight; ~150–300 k visible triangles; one real-time directional light and NO real-time shadows.
 */

import type { TierPreset } from './quality_tiers.js';

export type VrLevel = 0 | 1 | 2;
export const VR_LEVEL_NAMES = ['quality', 'balanced', 'performance'] as const;
export const VR_LEVEL_COUNT = 3;

type PerLevel<T> = readonly [T, T, T];
type VrValue<T> = T | PerLevel<T>;
export type VrDecision<T> =
  | { kind: 'vr'; value: VrValue<T>; why: string }
  | { kind: 'keep'; why: string };

type Decisions = { readonly [K in keyof Required<TierPreset>]: VrDecision<Required<TierPreset>[K]> };

const vr = <T>(value: VrValue<T>, why: string): VrDecision<T> => ({ kind: 'vr', value, why });
const keep = (why: string): { kind: 'keep'; why: string } => ({ kind: 'keep', why });
const L = <T>(quality: T, balanced: T, performance: T): PerLevel<T> => [quality, balanced, performance] as const;

const PREF = 'the player\'s preference';
const RELOAD = 'applies after a reload — a VR value would stay on the desktop after the session';
const NEXT_WORLD = 'applies at the next world entry — a VR value would stay on the desktop until then';
const PARSE = 'applied when a material is parsed — assets parsed in the headset would keep the VR look';
const POST = 'feeds the post chain, which is bypassed while presenting — minimal keeps the rebuilt chain cheap';
const SPACE = 'the space sector — built on entry, not live';

/** The decision for every TierPreset key. Order follows the interface. */
export const VR_DECISIONS: Decisions = {
  post: vr({ fxaa: { enabled: false } }, POST),
  pixelRatio: vr(1, 'the XR framebuffer scale sizes the eye buffers; the canvas ratio is not applied while presenting'),
  targetHeight: vr(0, 'the XR framebuffer scale sizes the eye buffers'),
  upscale: vr('off', 'FSR never runs in XR (the post chain is bypassed)'),
  upscaleMode: vr('fsr', POST),
  cloudResolution: vr(0.25, POST),
  adaptive: vr(false, 'the desktop FSR governor must not step while presenting; the XR governor owns the headset'),
  particleCap: vr(L(0.2, 0.1, 0), 'weather particles are scene geometry drawn for both eyes; on WebGL2 they are simulated on the headset CPU'),
  fogScale: vr(0.6, 'the night fog read as "the world goes dark past ~230 m" in the headset (2026-08-16 frames)'),
  cloudsEnabled: vr(false, 'the cloud composite is a post pass (bypassed); the mesh field is a ray-march per fragment'),
  shadowsEnabled: vr(false, 'no real-time shadows on a mobile GPU (Meta); CSM also fits cascades to a projection XR replaces'),
  lightingBoost: vr(1.35, 'compensates the light bloom / clouds / haze carry on the desktop (the potato rule)'),
  settlementRadiusScale: vr(L(0.6, 0.5, 0.45), 'draw calls: a city is the largest draw-call source in the game'),
  settlementForceLowLod: vr(true, 'the LOD1 civ packs are the 1024-class textures a headset caps to anyway'),
  distantDetail: vr(L(true, true, false), 'the far tier is 4 draws; performance drops it and the fog wall closes the view'),
  placementLod: vr(L(2, 3, 3), 'admin placements are 1,500 rows drawn world-wide'),
  settlementBuildLod: vr(2, 'the screen-space ladder tops out at LOD2 (16 % of LOD0)'),
  riverWater: vr('simple', 'a live switch; the realistic strip reads the screen for refraction on WebGL2'),
  groundQuality: keep(RELOAD),
  skyQuality: keep(RELOAD),
  look: vr('none', POST),
  foliageShading: vr(false, 'leaf translucency is per fragment on every tree'),
  fluidPhysics: vr(false, 'the ripple field steps on the CPU every frame'),
  materialFx: keep(PARSE),
  materialGrime: keep(PARSE),
  mipBias: keep(PARSE),
  propSurfaceDetail: keep(PARSE),
  cameraDof: vr('off', 'post-only, and a blur the eye cannot focus through is a sickness trigger'),
  cameraFNumber: keep(PREF),
  playerBodyRig: keep('the player\'s own body — hidden in first person, which is how the headset plays'),
  footIk: vr(L(true, false, false), 'versona foot IK samples the ground per foot on the CPU'),
  breathing: keep('a cheap bone sway on near versonas'),
  interiorMapping: keep(PARSE),
  vfxShaders: keep('materials built as they are first needed — not live'),
  litDecals: keep('decal pools are picked at placement — not live'),
  lampCookies: keep(RELOAD),
  waterFoam: keep(RELOAD),
  waterCaustics: keep(RELOAD),
  cloudShadows: vr(false, 'the clouds are off in VR, so their shadows must be too (the ground material key carries it)'),
  groundAntiTiling: vr(false, 'three extra taps per ground layer per fragment; the ground material key carries it'),
  groundMaterial: keep('a layout change rebuilds every resident chunk — a hitch at session start'),
  groundArrayRes: keep('a texture-array rebuild'),
  groundPotatoSet: keep('a texture-array rebuild'),
  groundTriplanar: vr(false, 'triplanar triples the ground samples per fragment; the ground material key carries it'),
  groundBands: vr(2, 'one band fewer per fragment; the ground material key carries it'),
  groundPom: vr('off', 'parallax marching per ground fragment'),
  groundNearSeg: keep('chunk mesh density — a rebuild of every resident chunk'),
  groundMicroShadow: vr(false, 'a per-fragment ground term; the ground material key carries it'),
  groundHexTiling: vr(false, 'a per-fragment ground term; the ground material key carries it'),
  groundPomShadow: vr(false, 'a second parallax march'),
  rainRipples: keep(RELOAD),
  aurora: vr(false, 'a marched sky band — fill on both eyes'),
  nebulae: keep(SPACE),
  spaceSky: keep(SPACE),
  spaceMotion: keep(SPACE),
  spacePlumes: keep(SPACE),
  spaceLitHulls: keep(SPACE),
  spaceShields: keep(SPACE),
  eclipseShadows: keep(SPACE), // shader gaps R4: uniforms on materials built at sector entry; no screen-space cost
  planetRings: keep(SPACE), // shader gaps R4: one transparent sheet per ringed planet, built at entry
  orbitalClouds: keep(SPACE), // shader gaps R4: a shell + a worker bake a minute, built at entry
  blackHole: vr('off', 'a per-pixel lens on the sky pass and a camera-centred cap, derived from ONE camera position — not proven per eye (no headset run); live: a placed hole hides on the quality change and the session boundary restores it'), // shader gaps: black hole
  planetAtmosphere: keep(SPACE),
  underwaterShafts: keep(RELOAD),
  dqSkinning: keep('patched once per body master — not live'),
  specularAA: keep(PARSE),
  proceduralFiltering: keep(PARSE),
  volumetricLights: vr(false, 'a shadowed ray-march volume'),
  litSmoke: keep('smoke materials are built with the fire — not live'),
  areaLights: keep(RELOAD),
  glassTransmission: keep(PARSE),
  hardSurfaceAnisotropy: keep(PARSE),
  retroreflection: keep(PARSE),
  impostorBlend: keep('applies at the next impostor bake'),
  rainShelter: vr(false, 'the roof-map render is an extra scene pass'),
  autoExposureMetering: keep('the meter reads the post chain, which is bypassed in XR'),
  fireQuality: vr('sprite', 'volume and sim fires are ray-marched per fragment'),
  fireLights: keep(RELOAD),
  fireHaze: vr(false, 'heat haze reads the screen behind it'),
  grassLayer: vr('off', 'grass blades are invisible at VR pixel density and cost thousands of instances'),
  geologyDensity: vr(L(0.25, 0.15, 0), 'rock scatter instances'),
  understoryDensity: vr(L(0.15, 0, 0), 'shrub scatter instances'),
  forestHorizonM: vr(0, 'the far forest ring'),
  farCanopy: vr(L(true, false, false), 'the far tier\'s canopy tint'),
  foliageJitter: keep('a per-instance transform jitter at planting — free per frame'),
  ambientLife: vr(false, 'ambient critters are CPU simulation and extra draws'),
  caveLight: vr('baked', 'no local probe in a headset (its capture + the crystals\' PMREM lookup); the baked field stays — VR has no shadows, and without the field the sun lights a cave floor through the hill'),
  materialDamage: keep(PARSE), // the graph is built at parse; in a headset the runtime turns a 'full' graph's crater normal + heat off LIVE (uDamageFull), the marks stay
  eonDiffuse: keep(PARSE), // shader gaps R4: the rock material's lighting model is built at its first use — a few ALU per light, no headset cost worth a second material
  laplacianBlend: keep(RELOAD), // shader gaps R4: built into the ground graph at its next build (the ground key); its coarse taps run only on the gravel ↔ rock band
  flakeGlints: keep(PARSE), // shader gaps R4: built into the slate's lighting model at first use; direct light only, footprint-filtered (no shimmer in stereo)
  windowShadows: keep(RELOAD), // shader gaps R5: built into the pooled window lights at init (like areaLights) — a VR value would stay on the desktop; the occluder term runs only with someone near a lit pane
  skinShading: keep('a material response on the few near rig actors (the tier caps them) — it reaches the headset where a screen-space pass cannot; one LUT fetch per light per skin fragment'),
  wetSurfaces: keep(PARSE), // shader gaps R3: the wet clock drives live uniforms (no headset cost); the prop layer is built at parse — a VR value would stay on the desktop's assets
  surfaceDeform: vr(false, 'the deformation field rides the roof map, which is off while presenting (its bake is an extra scene pass)'), // shader gaps R3
  volumeResolution: keep('the light volume itself is off in a headset (volumetricLights)'), // shader gaps R3 (agent F)
  volumeNoise: keep('the light volume itself is off in a headset (volumetricLights)'), // shader gaps R3 (agent F)
  fftOcean: vr(false, 'WebGPU compute — a headset presents on WebGL2, where the Gerstner sea draws; the sea rebuilds on the quality change, so the session boundary restores it'), // shader gaps R5
  puddleSim: vr('off', 'WebGPU compute — a headset presents on WebGL2, where the static puddles draw; the ground rebuilds on the quality change, so the session boundary restores it'), // shader gaps R6
  interiorDetail: vr(L(1, 0, 0), 'read when a room is entered — live at the session boundary; a headset gets the 256 px interior tiles at quality and the 128 px essential set below it (no wear layer, no glow add, the one generic room and no bed): texture memory and a second sampler per furniture fragment are what a mobile GPU pays for'), // interiors programme, slice D
  meshSmoothing: keep(PARSE),
  surfaceDetail: keep(PARSE),
  foliageDensityScale: vr(L(0.25, 0.12, 0), 'trees were the triangle whale (2026-08-16); a headset holds ~150–300 k visible triangles'),
  lodDistanceScale: vr(L(1.0, 0.8, 0.6), 'swap to the low LOD sooner'),
  shadowMapSize: vr(256, 'shadows are off in VR'),
  shadowCascades: vr(1, 'shadows are off in VR'),
  shadowMaxFar: vr(40, 'shadows are off in VR'),
  moonShadows: vr(false, 'shadows are off in VR'),
  farCull: vr(L(200, 160, 120), 'the object draw distance — Quest 3 160 m, Quest 2 120 m (report §2.4)'),
  chunkRadius: vr(2, 'a 512 m terrain window, the floor the draw distance allows'),
  foliageRing: vr(1, 'foliage on the nearest chunk ring only'),
  maxTextureRes: keep(PARSE),
  nearActors: vr(L(6, 4, 2), 'skinned versona actors near the player — each is a skinned draw'),
  nearFoliageLod: vr(2, 'near trees at LOD2 (15 % of LOD0)'),
  propLod: vr(2, 'props and decor at LOD2'),
  farBodyLod: vr(2, 'far versona bodies at LOD2'),
  drawCallBudget: vr(150, 'Meta: ~100 comfortable, 200 tight (the HUD warns above it)'),
  memoryBudgetMb: vr(512, 'a standalone headset shares memory with the OS and the compositor'),
  prefetchRegions: vr(1, 'one region ahead'),
  aerialEnabled: keep(RELOAD),
  autoExposure: keep('the meter reads the post chain (bypassed); the exposure it last set stays'),
  lowClouds: vr(false, 'a cloud layer — the clouds are off in VR'),
  cloudParallax: keep(PREF),
  aaQuality: keep(POST),
  autoExposureStrength: keep(PREF),
  anisotropy: keep('sampler state is set when a texture is built'),
  tonemapExposure: keep(PREF),
  tonemapOperator: keep(PREF),
  exposureMode: keep(PREF),
  autoExposureRangeEv: keep(PREF),
  highClouds: keep('applies at the next world entry (the cirrus layer)'),
  cloudMarchQuality: vr('low', 'the clouds are off in VR'),
  waterReflections: keep(NEXT_WORLD),
  waterSkyReflection: keep(NEXT_WORLD),
  waterCloudReflection: keep(NEXT_WORLD),
  waterSpectrumNormals: keep(NEXT_WORLD),
  waterScattering: keep(NEXT_WORLD),
  waterGlitterFilter: keep(NEXT_WORLD),
  waterFarSea: keep(NEXT_WORLD),
  waterAnalyticBed: keep(NEXT_WORLD),
  waterBedCaustics: keep(NEXT_WORLD),
  waterUnderwaterMedium: keep(NEXT_WORLD),
  waterMarineLife: vr(false, 'marine snow is a particle system'),
  waterCalmLakes: keep(NEXT_WORLD),
  waterfallFx: vr(false, 'waterfall mist and strands are particle systems'),
  waterReflectionBudget: keep(NEXT_WORLD),
  waterSplashCrowns: vr(false, 'splash crowns are particle systems'),
  waterFoamField: keep(NEXT_WORLD),
  waterRiverFlow: keep(NEXT_WORLD),
  colorblindMode: keep(PREF),
  colorblindStrength: keep(PREF),
  cameraFov: keep('the headset sets its own field of view; the desktop value returns on exit'),
  foliageWind: vr(L(true, true, false), 'a vertex-stage sway — a still forest reads as dead in a headset'),
  autoTune: vr(false, 'the desktop fps tuner steps the saved preset; the XR governor owns the headset'),
};

function pick<T>(v: VrValue<T>, level: VrLevel): T {
  return (Array.isArray(v) ? (v as PerLevel<T>)[level] : v) as T;
}

/** Deep-enough copy of a preset: `post` is its only nested object (and its sub-configs are replaced whole). */
export function clonePresetShallow(p: TierPreset): TierPreset {
  return { ...p, post: { ...p.post } };
}

/**
 * The preset a VR session runs: the live (desktop) preset with every `vr` decision applied at `level`.
 * Never mutates `live`. `keep` keys are the live object's own values — including an absent optional key.
 */
export function vrPreset(live: TierPreset, level: VrLevel): TierPreset {
  const out = clonePresetShallow(live) as unknown as Record<string, unknown>;
  for (const key of Object.keys(VR_DECISIONS) as Array<keyof TierPreset>) {
    const d = VR_DECISIONS[key] as VrDecision<unknown>;
    if (d.kind !== 'vr') continue;
    const v = pick(d.value, level);
    out[key] = key === 'post' ? JSON.parse(JSON.stringify(v)) : v;
  }
  return out as unknown as TierPreset;
}

/**
 * Put a VR-overlaid live preset back EXACTLY as the snapshot was. `Object.assign` alone is not enough: an optional
 * key the desktop preset never carried (`foliageWind` "follows foliageShading" when absent) would keep the VR value
 * after the session — so every key the snapshot lacks is deleted. Mutates `live` in place (the 47 modules that read
 * `activePreset()` hold that object).
 */
export function restorePreset(live: TierPreset, snapshot: TierPreset): void {
  const l = live as unknown as Record<string, unknown>;
  const s = snapshot as unknown as Record<string, unknown>;
  for (const key of Object.keys(l)) if (!(key in s)) delete l[key];
  Object.assign(l, s, { post: { ...snapshot.post } });
}

/** Overlay a VR level onto the live preset in place (the inverse of `restorePreset` from the same snapshot). */
export function applyVrPreset(live: TierPreset, snapshot: TierPreset, level: VrLevel): void {
  restorePreset(live, snapshot);
  const next = vrPreset(snapshot, level) as unknown as Record<string, unknown>;
  Object.assign(live as unknown as Record<string, unknown>, next);
}

/** The keys a VR level changes, and what to. For the Perf page and the harness. */
export function vrChanges(live: TierPreset, level: VrLevel): Array<{ key: string; from: unknown; to: unknown }> {
  const next = vrPreset(live, level) as unknown as Record<string, unknown>;
  const cur = live as unknown as Record<string, unknown>;
  const out: Array<{ key: string; from: unknown; to: unknown }> = [];
  for (const key of Object.keys(VR_DECISIONS)) {
    if (JSON.stringify(cur[key]) !== JSON.stringify(next[key])) out.push({ key, from: cur[key], to: next[key] });
  }
  return out;
}

/** Clamp any number to a legal VR level. */
export function clampLevel(n: number): VrLevel {
  if (!Number.isFinite(n)) return 2; // NaN compares false both ways and Math.round(NaN) is NaN — not a level; the cheapest is the safe answer
  return (n <= 0 ? 0 : n >= 2 ? 2 : Math.round(n)) as VrLevel;
}
